import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Play, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';

export default function Videos({ user }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data);
    } catch (error) {
      console.error('Failed to load videos', error);
    } finally {
      setLoading(false);
    }
  };

  const openVideoDetail = (video) => {
    setSelectedVideo(video);
    setCurrentImageIndex(0);
  };

  const closeVideoDetail = () => {
    setSelectedVideo(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedVideo?.album_images) {
      setCurrentImageIndex((prev) => 
        prev < selectedVideo.album_images.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (selectedVideo?.album_images) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : selectedVideo.album_images.length - 1
      );
    }
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="videos-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">小學堂</h1>

        {loading ? (
          <div>載入中...</div>
        ) : videos.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">暫無影片</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div 
                key={video.video_id} 
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer" 
                data-testid="video-item"
                onClick={() => openVideoDetail(video)}
              >
                <div className="relative">
                  {video.thumbnail_url ? (
                    <img 
                      src={video.thumbnail_url.startsWith('http') 
                        ? video.thumbnail_url 
                        : `${window.location.origin}/api/media-files${video.thumbnail_url.replace('/media', '')}`}
                      alt={video.title}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Play className="text-white" size={64} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Play className="text-white" size={48} />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-800 mb-2">{video.title}</h3>
                  {video.intro_text && (
                    <p className="text-gray-600 text-sm mb-2">{video.intro_text}</p>
                  )}
                  {video.album_images && video.album_images.length > 0 && (
                    <p className="text-xs text-gray-500">📸 {video.album_images.length} 張相片</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Video Detail Dialog */}
        {selectedVideo && (
          <Dialog open={!!selectedVideo} onOpenChange={closeVideoDetail}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <button
                onClick={closeVideoDetail}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
              >
                <X size={20} />
              </button>

              <div className="space-y-6">
                {/* Video Player - Fixed aspect ratio */}
                <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '400px' }}>
                  <video 
                    controls 
                    autoPlay
                    className="w-full h-auto max-h-[70vh]"
                    style={{ objectFit: 'contain' }}
                    src={selectedVideo.video_url.startsWith('http') 
                      ? selectedVideo.video_url 
                      : `${window.location.origin}/api/media-files${selectedVideo.video_url.replace('/media', '')}`}
                  >
                    您的瀏覽器不支持視頻播放
                  </video>
                </div>

                {/* Video Info */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-3">{selectedVideo.title}</h2>
                  
                  {selectedVideo.intro_text && (
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <p className="text-gray-700">{selectedVideo.intro_text}</p>
                    </div>
                  )}

                  {selectedVideo.description && (
                    <div className="text-gray-600">
                      <h3 className="font-semibold mb-2">詳細描述：</h3>
                      <p className="whitespace-pre-wrap">{selectedVideo.description}</p>
                    </div>
                  )}
                </div>

                {/* Album Images */}
                {selectedVideo.album_images && selectedVideo.album_images.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">相冊 ({selectedVideo.album_images.length} 張)</h3>
                    
                    {/* Main Image Display */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4">
                      <img 
                        src={selectedVideo.album_images[currentImageIndex].startsWith('http') 
                          ? selectedVideo.album_images[currentImageIndex] 
                          : `${window.location.origin}/api/media-files${selectedVideo.album_images[currentImageIndex].replace('/media', '')}`}
                        alt={`相冊 ${currentImageIndex + 1}`}
                        className="w-full h-96 object-contain"
                      />
                      
                      {selectedVideo.album_images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                          >
                            <ChevronLeft size={24} />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
                          >
                            <ChevronRight size={24} />
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {currentImageIndex + 1} / {selectedVideo.album_images.length}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Thumbnail Strip */}
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedVideo.album_images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.startsWith('http') 
                            ? img 
                            : `${window.location.origin}/api/media-files${img.replace('/media', '')}`}
                          alt={`縮略圖 ${idx + 1}`}
                          className={`w-20 h-20 object-cover rounded cursor-pointer ${
                            idx === currentImageIndex ? 'ring-4 ring-blue-500' : 'opacity-60 hover:opacity-100'
                          }`}
                          onClick={() => setCurrentImageIndex(idx)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
